import React from "react";
import { SEQUENCE_FALLBACK, UNSCALED_SPHERE } from "./lib/palette";
import { sphereHex, sphereQuadrants } from "./lib/colorScales";
// Malkuth alone is drawn in four colors, quartered for the four elements.
// Index 0 runs 12:00->3:00 and they continue clockwise, matching
// docs/tree-reference-hermetic.svg exactly.
export function quadrantPath(cx, cy, r, index) {
  const corners = [
    [cx, cy - r],
    [cx + r, cy],
    [cx, cy + r],
    [cx - r, cy]
  ];
  const [fromX, fromY] = corners[index];
  const [toX, toY] = corners[(index + 1) % 4];
  return `M ${cx} ${cy} L ${fromX} ${fromY} A ${r} ${r} 0 0 1 ${toX} ${toY} Z`;
}

export default function Sphere({
  sephirah,
  radius,
  scale,
  isSelected,
  isEndpoint,
  inSequence = false,
  isSequenceCurrent = false,
  sequenceColour = null,
  tabIndex,
  onSelect,
  onKeyDown,
  nodeRef
}) {
  const { slug, number, name, title, planet, x, y, pillar } = sephirah;

  // A tradition with no colour scales carries one neutral fill instead. Which
  // COLUMN the scale reads is the scale's own business — see lib/colorScales.
  const fill = sphereHex(sephirah, scale) || sephirah.colorHex || UNSCALED_SPHERE;
  const quadrants = sphereQuadrants(sephirah, scale);
  const quartered = quadrants.length === 4;

  // Da'at is drawn but not counted among the ten, so it is hollow and dashed —
  // and it carries its name rather than a numeral, because it has no ordinal.
  const counted = sephirah.countedInTen !== false;

  const className = [
    "sphere",
    counted ? "" : "sphere--uncounted",
    isSelected ? "sphere--selected" : "",
    isEndpoint ? "sphere--endpoint" : "",
    inSequence ? "sphere--in-sequence" : "",
    isSequenceCurrent ? "sphere--sequence-current" : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <g
      ref={nodeRef}
      role="button"
      tabIndex={tabIndex}
      aria-pressed={isSelected}
      aria-label={
        [`${name}, ${title}.`, planet, counted ? null : "Not counted among the ten."]
          .filter(Boolean)
          .join(" ")
      }
      onClick={(event) => {
        event.stopPropagation();
        onSelect();
      }}
      onKeyDown={onKeyDown}
      className={className}
      data-testid={`sphere-${number}`}
      data-slug={slug}
      data-name={name}
    >
      {/* Hit target. `transparent` is hit-testable; `none` is not. */}
      <circle cx={x} cy={y} r={radius + 4} fill="transparent" />

      {!counted ? (
        <circle
          className="sphere__body sphere__body--uncounted"
          cx={x}
          cy={y}
          r={radius}
          fill="none"
          stroke={fill}
          data-fill={fill}
        />
      ) : quartered ? (
        <g className="sphere__quadrants">
          {quadrants.map((color, index) => (
            <path
              key={index}
              d={quadrantPath(x, y, radius, index)}
              fill={color}
              data-testid={`quadrant-${number}-${index}`}
            />
          ))}
          <circle
            className="sphere__body sphere__body--hollow"
            cx={x}
            cy={y}
            r={radius}
            fill="none"
          />
        </g>
      ) : (
        <circle
          className="sphere__body"
          cx={x}
          cy={y}
          r={radius}
          fill={fill}
          data-fill={fill}
        />
      )}

      {/* Every sphere the sequence has reached, not only the cursor. */}
      {inSequence && (
        <circle
          className="sphere__sequence-ring"
          cx={x}
          cy={y}
          r={radius + 1.6}
          fill="none"
          stroke={sequenceColour || SEQUENCE_FALLBACK}
          data-testid={`sequence-reached-${slug}`}
        />
      )}

      {isSelected && (
        <circle className="sphere__ring" cx={x} cy={y} r={radius + 2.6} fill="none" />
      )}

    </g>
  );
}
